// Bestand buchen — Wareneingang oder Abgang.
//
// Ein Wareneingang bucht beim Lieferanten: vorbelegt ist der des Artikels,
// dazu der Einkaufspreis. Liegt die Rechnung bei, kann ein Administrator sie
// gleich als Eingangsrechnung erfassen — bezahlt oder offen mit Fälligkeit.
// Der Betrag ist aus Menge × Einkaufspreis + Umsatzsteuer vorgerechnet.
import { useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { euro, menge as mengeText } from '../../../gemeinsam/lib/daten';
import { einheitAnzeige } from '../lib/einheiten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import type { Artikel, Lieferant } from '../../../gemeinsam/lib/typen';
import { Auswahl, Blickfang, Feld, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';

const zahl = (s: string) => { const t = s.trim(); return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t) || 0; };
const inTagen = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function BestandDialog({ artikel, richtung, lieferanten, istAdmin, beiFertig, beiAbbruch }: {
  artikel: Artikel; richtung: 1 | -1; lieferanten: Lieferant[]; istAdmin: boolean;
  beiFertig: (meldung: string) => void; beiAbbruch: () => void;
}) {
  const { t } = useSprache();
  const zugang = richtung > 0;
  const [menge, setMenge] = useState('1');
  const [grund, setGrund] = useState(zugang ? 'Lieferung' : 'Korrektur');
  const [lieferantId, setLieferantId] = useState(artikel.lieferantId ?? '');
  const [ek, setEk] = useState(String(artikel.einkaufspreis ?? '').replace('.', ','));
  const [mitRechnung, setMitRechnung] = useState(false);
  const [nummer, setNummer] = useState('');
  const [betrag, setBetrag] = useState('');
  const [bezahlt, setBezahlt] = useState(false);
  const [faelligAm, setFaelligAm] = useState(inTagen(14));
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  const vorschlag = Math.round(zahl(menge) * zahl(ek) * (1 + (artikel.steuersatz ?? 19) / 100) * 100) / 100;

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    const anzahl = zahl(menge);
    if (!(anzahl > 0)) { setFehler('Bitte eine positive Menge angeben.'); return; }
    setSendet(true); setFehler(null);
    try {
      await api(`/products/${artikel.id}/bestand`, {
        methode: 'POST',
        daten: {
          menge: richtung * anzahl, art: zugang ? 'wareneingang' : 'korrektur', grund,
          ...(zugang ? { lieferantId: lieferantId || null, einkaufspreis: ek.trim() ? zahl(ek) : undefined } : {}),
          ...(zugang && mitRechnung ? {
            rechnung: {
              nummer, brutto: betrag.trim() ? zahl(betrag) : undefined,
              bezahlt, faelligAm: bezahlt ? undefined : faelligAm,
            },
          } : {}),
        },
      });
      beiFertig(t('lager.gebucht').replace('{name}', artikel.name));
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : 'Buchung fehlgeschlagen.');
    } finally { setSendet(false); }
  }

  return (
    <Blickfang>
      <form onSubmit={speichern} className="space-y-3 rounded-xl border border-marke-200 bg-marke-50/50 p-4 dark:border-marke-900 dark:bg-marke-950/20">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {zugang ? t('lager.zugang') : t('lager.abgang')}: {artikel.name}
          <span className="ml-2 font-normal text-slate-500">
            {t('feld.bestand')} {mengeText(artikel.bestand, einheitAnzeige(artikel.einheit, t))}
          </span>
        </p>
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <div className={`grid gap-3 ${zugang ? 'sm:grid-cols-4' : 'sm:grid-cols-2'}`}>
          <Feld label={`${t('feld.menge')} (${einheitAnzeige(artikel.einheit, t)})`} value={menge} inputMode="decimal" autoFocus required
                onChange={e => setMenge(e.target.value)} />
          {zugang && (
            <Auswahl label={t('feld.lieferant')} value={lieferantId} onChange={e => setLieferantId(e.target.value)}>
              <option value="">— {t('lager.ohneLieferant')} —</option>
              {lieferanten.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </Auswahl>
          )}
          {zugang && (
            <Feld label={t('lager.einkaufspreis')} value={ek} inputMode="decimal" onChange={e => setEk(e.target.value)} />
          )}
          <Feld label={t('feld.grund')} value={grund} onChange={e => setGrund(e.target.value)} />
        </div>

        {zugang && istAdmin && (
          <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={mitRechnung} onChange={e => setMitRechnung(e.target.checked)} />
              {t('lager.rechnungLiegtBei')}
            </label>
            {mitRechnung && (
              <div className="grid gap-3 sm:grid-cols-3">
                <Feld label={t('lieferanten.rechnungsnummer')} value={nummer} onChange={e => setNummer(e.target.value)} />
                <Feld label={t('lieferanten.betrag')} value={betrag} inputMode="decimal"
                      placeholder={vorschlag > 0 ? euro(vorschlag) : ''}
                      hinweis={vorschlag > 0 && !betrag ? `${euro(vorschlag)} = ${menge} × ${ek || 0} + USt` : undefined}
                      onChange={e => setBetrag(e.target.value)} />
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <input type="checkbox" checked={bezahlt} onChange={e => setBezahlt(e.target.checked)} />
                    {t('lager.rechnungBezahlt')}
                  </label>
                  {!bezahlt && (
                    <Feld label={t('lieferanten.faelligAm')} type="date" value={faelligAm} onChange={e => setFaelligAm(e.target.value)} />
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Knopf type="submit" variante="primaer" laedt={sendet}>{t('lager.buchen')}</Knopf>
          <Knopf type="button" onClick={beiAbbruch}>{t('aktion.abbrechen')}</Knopf>
        </div>
      </form>
    </Blickfang>
  );
}
